import MenuButton from "app/ui/MenuButton";
import { type TutorialLevel } from "./tutorial-data";
import TUTORIAL_LEVEL_ORDER from "./tutorial-level-order";
import cn from "app/util/classnames";
import useGameState from "app/game/useGameState";
import { useCallback } from "react";

interface TutorialLevelListProps {
  className?: string;
  onLevelSelected: (level: TutorialLevel) => void;
  isInOnboardingFlow?: boolean;
}

export default function TutorialLevelList({
  className = "",
  onLevelSelected,
  isInOnboardingFlow = true,
}: TutorialLevelListProps) {
  const { completedTutorialLevels } = useGameState();

  return (
    <ul
      className={cn(
        "w-auto",
        "flex flex-col items-start justify-center",
        className,
      )}
    >
      {TUTORIAL_LEVEL_ORDER.map((level: TutorialLevel, i: number) => {
        let state: "locked" | "unlocked" | "upNext" = "unlocked";
        if (isInOnboardingFlow) {
          const numCompleted = completedTutorialLevels?.size ?? 0;
          if (numCompleted < i) {
            state = "locked";
          } else if (isInOnboardingFlow && numCompleted == i) {
            state = "upNext";
          }
        }
        return (
          <ListEntry
            key={i}
            isLast={i === TUTORIAL_LEVEL_ORDER.length - 1}
            levelNumber={i + 1}
            levelName={level.name}
            levelDescription={level.description}
            isCompleted={completedTutorialLevels?.has(level.name) ?? false}
            onStartClick={() => onLevelSelected(level)}
            state={state}
          />
        );
      })}
    </ul>
  );
}

interface ListEntryProps {
  isLast: boolean;
  levelNumber: number;
  levelName: string;
  levelDescription: string;
  isCompleted: boolean;
  onStartClick: () => void;
  state: "locked" | "upNext" | "unlocked";
}

function ListEntry({
  isLast,
  levelNumber,
  levelName,
  levelDescription,
  isCompleted,
  onStartClick,
  state = "unlocked",
}: ListEntryProps) {
  const handleClick = useCallback(() => {
    if (state !== "locked") {
      onStartClick();
    }
  }, [state, onStartClick]);

  return (
    <li
      className={cn(
        "w-full",
        "px-3 pt-2 pb-2.5",
        "group",
        "border border-green-darker",
        state !== "locked" && "hover:bg-green-highlight cursor-pointer",
        !isLast && "border-b-0",
        levelNumber !== 1 && "border-t-green-dark",
      )}
      onClick={handleClick}
    >
      <div
        className={cn(
          "flex flex-col gap-1",
          state === "locked"
            ? "opacity-40"
            : isCompleted
              ? "opacity-60 group-hover:opacity-100"
              : "opacity-100",
        )}
      >
        <div className={cn("flex flex-col")}>
          <div // Title text
            className="flex justify-start items-center gap-2"
          >
            <span className="font-semibold">{`${levelNumber}.`}</span>
            <span
              className={cn(
                "text-lg font-semibold",
                isCompleted && "line-through",
              )}
            >
              {levelName}
            </span>
            {isCompleted && <span className="italic">Completed!</span>}
          </div>
        </div>
        <div className="grow hidden md:visible" />
        <div className="flex flex-col md:flex-row justify-center items-center md:items-start min-w-22 gap-1 md:gap-5 md:pr-2">
          <p // Description
            className="md:ml-5.5 grow"
          >
            {levelDescription}
          </p>
          <MenuButton
            doPing={state === "upNext"}
            variant={isCompleted ? "small" : undefined}
            className={cn(
              "pointer-events-none",
              "py-1 px-0 md:px-2 w-auto",
              "text-md font-condensed uppercase",
              "relative",
              isCompleted
                ? "min-w-20 md:min-w-18 group-hover:bg-black group-hover:text-yellow"
                : "min-w-22 md:min-w-20 min-h-9 bg-black text-green-mid",
              state === "upNext" && "text-yellow",
            )}
            onClick={onStartClick}
          >
            {state === "locked" ? "Locked" : isCompleted ? "Replay" : "Start"}
          </MenuButton>
        </div>
      </div>
    </li>
  );
}
